import React, { useState } from 'react';
import { TagIcon } from '@/src/components/icons';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/src/components/ui/dialog';
import { Badge } from '@/src/components/ui/badge';
import { Button } from '@/src/components/ui/button';
import { Input } from '@/src/components/ui/input';

const PropertyTagManager = ({ 
  property, 
  isOpen, 
  onClose, 
  getPropertyTags, 
  getAllTags, 
  addPropertyToTag, 
  removePropertyFromTag, 
  createNewTag,
  updateTag,
  deleteTag
}) => {
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#3B82F6');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [editingTag, setEditingTag] = useState(null);
  const [editTagName, setEditTagName] = useState('');
  const [editTagColor, setEditTagColor] = useState('');
  
  const propertyTags = getPropertyTags(property?.roll_number) || [];
  const allTags = getAllTags();
  const availableTags = allTags.filter(tag => !propertyTags.find(pt => pt.id === tag.id));
  
  const colors = [
    '#3B82F6', '#EF4444', '#10B981', '#F59E0B', 
    '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'
  ];
  
  const handleCreateTag = () => {
    if (newTagName.trim()) {
      const tagId = createNewTag(newTagName.trim(), newTagColor);
      addPropertyToTag(property.roll_number, tagId);
      setNewTagName('');
      setShowCreateForm(false);
      setRefresh(r => r + 1);
    }
  };
  
  const handleAddTag = (tagId) => {
    addPropertyToTag(property.roll_number, tagId);
    setRefresh(r => r + 1);
  };
  
  const handleRemoveTag = (tagId) => {
    removePropertyFromTag(property.roll_number, tagId);
    setRefresh(r => r + 1);
  };

  const handleEditTag = (tag) => {
    setEditingTag(tag.id);
    setEditTagName(tag.name);
    setEditTagColor(tag.color);
  };

  const handleSaveEdit = () => {
    if (editTagName.trim() && editingTag) {
      updateTag(editingTag, { name: editTagName.trim(), color: editTagColor });
      setEditingTag(null);
      setEditTagName('');
      setEditTagColor('');
      setRefresh(r => r + 1);
    }
  };

  const handleCancelEdit = () => {
    setEditingTag(null);
    setEditTagName('');
    setEditTagColor('');
  };

  const handleDeleteTag = (tagId) => {
    if (window.confirm('Are you sure you want to delete this tag? It will be removed from all properties.')) {
      deleteTag(tagId);
      setRefresh(r => r + 1);
    }
  };
  
  console.log('PropertyTagManager render:', { isOpen, property: property?.roll_number });
  
  if (!isOpen || !property) {
    console.log('PropertyTagManager returning null:', { isOpen, hasProperty: !!property });
    return null;
  }
  
  console.log('Components check:', { 
    Dialog: !!Dialog, 
    DialogContent: !!DialogContent, 
    Badge: !!Badge, 
    Button: !!Button 
  });
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md" open={isOpen} onClose={onClose}>
        <DialogHeader>
          <DialogTitle>Manage Tags</DialogTitle>
          <DialogDescription>
            Add or remove tags for {property.full_address}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-medium mb-2">Current Tags</h4>
            {propertyTags.length === 0 ? (
              <p className="text-sm text-gray-500">No tags assigned</p>
            ) : (
              <div className="flex flex-wrap gap-1">
                {propertyTags.map(tag => (
                  <Badge
                    key={tag.id}
                    removable={true}
                    onRemove={() => handleRemoveTag(tag.id)}
                    style={{ backgroundColor: tag.color, color: '#ffffff', border: 'none' }}
                  >
                    {tag.name}
                  </Badge>
                ))}
              </div>
            )}
          </div>
          
          <div>
            <h4 className="text-sm font-medium mb-2">Available Tags</h4>
            {availableTags.length === 0 ? (
              <p className="text-sm text-gray-500">No other tags available</p>
            ) : (
              <div className="flex flex-wrap gap-1">
                {availableTags.map(tag => (
                  <Badge
                    key={tag.id}
                    variant="outline"
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => handleAddTag(tag.id)}
                    style={{ borderColor: tag.color, color: tag.color }}
                  >
                    + {tag.name}
                  </Badge>
                ))}
              </div>
            )}
          </div>
          
          <div>
            <h4 className="text-sm font-medium mb-2">Manage All Tags</h4>
            {allTags.length === 0 ? (
              <p className="text-sm text-gray-500">No tags created yet</p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {allTags.map(tag => (
                  <div key={tag.id} className="flex items-center gap-2 p-2 border rounded-lg">
                    {editingTag === tag.id ? (
                      <>
                        <Input
                          value={editTagName}
                          onChange={(e) => setEditTagName(e.target.value)}
                          className="flex-1"
                          size="sm"
                        />
                        <div className="flex gap-1">
                          {colors.map(color => (
                            <button
                              key={color}
                              className={`w-5 h-5 rounded-full border ${editTagColor === color ? 'border-gray-900' : 'border-gray-300'}`}
                              style={{ backgroundColor: color }}
                              onClick={() => setEditTagColor(color)}
                            />
                          ))}
                        </div>
                        <button
                          onClick={handleSaveEdit}
                          className="text-green-600 hover:text-green-700 text-xs px-2 py-1"
                        >
                          Save
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="text-gray-600 hover:text-gray-700 text-xs px-2 py-1"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <Badge style={{ backgroundColor: tag.color, color: '#ffffff', border: 'none' }}>
                          {tag.name}
                        </Badge>
                        <span className="text-xs text-gray-500 flex-1">
                          {tag.properties?.length || 0} {tag.properties?.length === 1 ? 'property' : 'properties'}
                        </span>
                        <button
                          onClick={() => handleEditTag(tag)}
                          className="text-blue-600 hover:text-blue-700 text-xs px-2 py-1"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteTag(tag.id)}
                          className="text-red-600 hover:text-red-700 text-xs px-2 py-1"
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div>
            {!showCreateForm ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCreateForm(true)}
                className="w-full"
              >
                <TagIcon />
                Create New Tag
              </Button>
            ) : (
              <div className="space-y-3 p-3 border rounded-lg">
                <div>
                  <label className="text-sm font-medium">Tag Name</label>
                  <Input
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.target.value)}
                    placeholder="Enter tag name"
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Color</label>
                  <div className="flex gap-2 mt-1">
                    {colors.map(color => (
                      <button
                        key={color}
                        className={`w-6 h-6 rounded-full border-2 ${newTagColor === color ? 'border-gray-900' : 'border-gray-300'}`}
                        style={{ backgroundColor: color }}
                        onClick={() => setNewTagColor(color)}
                      />
                    ))}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={handleCreateTag}
                    disabled={!newTagName.trim()}
                  >
                    Create
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowCreateForm(false);
                      setNewTagName('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PropertyTagManager;